/**
 * Whether Ember's content tables load: palettes.json, marks.json, styles.json, humanoids.json, each
 * on its own and against the others (a style's skin set, a humanoid's style and build, a look's
 * skin, hair and eyes, a race's body). Whether the result draws well is the renderer's question;
 * this is whether it can be drawn at all.
 *
 * Pure: imports no catalog (the content compiler validates before there is one), so the vocabulary
 * the engine knows -- mark anchors, clip sets -- is spelled out here and checked against the
 * engine's own by the tests.
 */
/** Where a mark can be pinned: a point of the trunk, or a limb's joint. */
export const MARK_CENTERS = ["head", "neck", "chest", "pelvis"] as const
export const MARK_JOINTS = ["shoulder", "elbow", "hand", "knee", "ankle"] as const
/** Which limb a mark is on: front and back by screen side, side-on by nearness to the camera. */
export const MARK_SIDES = ["screenLeft", "screenRight", "near", "far"] as const
/** The steps of a ramp a pattern letter may paint. */
export const RAMP_STEPS = ["light", "base", "shade", "deep", "line", "outline"] as const
/** The clip sets a style may name (styles.ts EMBER_CLIP_SETS). */
export const CLIP_SET_NAMES = ["natural"] as const
export const POSTURES = ["shamble"] as const

type Problem = { file: string; message: string }
const isObject = (v: unknown): v is Record<string, any> => !!v && typeof v === "object" && !Array.isArray(v)
const isHex = (c: unknown) => typeof c === "string" && /^#[0-9a-fA-F]{6}$/.test(c)
const isNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v)
const own = (o: unknown, k: unknown): boolean => isObject(o) && typeof k === "string" && Object.prototype.hasOwnProperty.call(o, k)
const entries = (o: unknown): [string, any][] => (isObject(o) ? Object.entries(o) : [])
/** A letter table's letters (a table may carry an `about`). */
const letters = (o: unknown): [string, any][] => entries(o).filter(([k]) => k !== "about")

export interface EmberTables {
    palettes: unknown
    marks: unknown
    styles: unknown
    humanoids: unknown
    /** For a look's references: hair styles (hair.json), body models (bodies.json), equipment models by slot (equipment.json). */
    hair?: unknown
    bodies?: unknown
    equipment?: unknown
}

export const emberTableProblems = (t: EmberTables): Problem[] => {
    const problems: Problem[] = []
    const fail = (file: string, message: string) => problems.push({ file, message })

    // ---- palettes ---------------------------------------------------------------------------------
    const pal = t.palettes as any
    const eyeColors = isObject(pal?.eye?.colors) ? pal.eye.colors : {}
    if (!isObject(pal)) fail("palettes", "expected an object")
    else {
        if (pal.version !== 1) fail("palettes", "version: expected 1")
        if (!isObject(pal.ramps)) fail("palettes", "ramps: expected name -> { tones }")
        for (const [name, group] of entries(pal.ramps)) {
            if (!isObject(group?.tones)) { fail("palettes", `ramps.${name}: expected { tones }`); continue }
            for (const [tone, r] of entries(group.tones)) if (!Array.isArray(r) || r.length !== 6 || !r.every(isHex)) fail("palettes", `ramps.${name}.tones.${tone}: six #rrggbb colours, light to outline`)
        }
        if (!own(pal.ramps?.bones?.tones, "white")) fail("palettes", "ramps.bones.tones.white: the bone a skeleton has unless it is named")
        if (!isObject(pal.skins)) fail("palettes", "skins: expected name -> ramp group names")
        for (const [name, list] of entries(pal.skins)) {
            if (!Array.isArray(list) || !list.every(g => typeof g === "string")) { fail("palettes", `skins.${name}: a list of ramp group names`); continue }
            for (const group of list) if (!own(pal.ramps, group)) fail("palettes", `skins.${name}: no ramp group ${group}`)
            if (!list.some(group => own(pal.ramps?.[group]?.tones, "light"))) fail("palettes", `skins.${name}: needs a \`light\` skin (what an unknown skin is drawn as)`)
        }
        for (const key of ["eye", "dyes", "hair"]) {
            if (!isObject(pal[key]?.colors)) { fail("palettes", `${key}.colors: expected name -> #rrggbb`); continue }
            for (const [name, c] of entries(pal[key].colors)) if (!isHex(c)) fail("palettes", `${key}.colors.${name}: #rrggbb`)
        }
        if (!own(pal.dyes?.colors, "gray")) fail("palettes", "dyes.colors.gray: what an unknown dye is drawn as")
        if (!own(pal.hair?.colors, "brown")) fail("palettes", "hair.colors.brown: what an unknown hair colour is drawn as")
    }
    const skinSet = (name: unknown): Set<string> | undefined => {
        if (!own(pal?.skins, name) || !Array.isArray(pal.skins[name as string])) return undefined
        return new Set((pal.skins[name as string] as string[]).flatMap(group => Object.keys(isObject(pal.ramps?.[group]?.tones) ? pal.ramps[group].tones : {})))
    }

    // ---- marks and eyes ---------------------------------------------------------------------------
    const marks = t.marks as any
    const colorRef = (ref: unknown) =>
        isHex(ref) || (typeof ref === "string" && ((ref.startsWith("skin.") && (RAMP_STEPS as readonly string[]).includes(ref.slice(5))) || (ref.startsWith("eye.") && own(eyeColors, ref.slice(4)))))
    const rowsOk = (rows: unknown, known: Set<string>, at: string) => {
        if (!Array.isArray(rows) || !rows.length || !rows.every(r => typeof r === "string" && r.length)) { fail("marks", `${at}: a list of pattern rows`); return }
        if (rows.some(r => r.length !== rows[0].length)) fail("marks", `${at}: every row the same width`)
        for (const ch of new Set(rows.join(""))) if (ch !== "." && !known.has(ch)) fail("marks", `${at}: no colour for '${ch}'`)
    }
    if (!isObject(marks)) fail("marks", "expected an object")
    else {
        if (marks.version !== 1) fail("marks", "version: expected 1")
        for (const [letter, step] of letters(marks.markLetters)) if (!(RAMP_STEPS as readonly string[]).includes(step)) fail("marks", `markLetters.${letter}: a ramp step (${RAMP_STEPS.join(", ")})`)
        const markLetters = new Set(letters(marks.markLetters).map(([k]) => k))
        if (!isObject(marks.markSets)) fail("marks", "markSets: expected name -> mark set")
        for (const [name, set] of entries(marks.markSets)) {
            const at = `markSets.${name}`
            if (!isObject(set)) { fail("marks", `${at}: expected an object`); continue }
            if (!Number.isInteger(set.frame) || set.frame < 16) fail("marks", `${at}.frame: the frame the marks were read off, in pixels`)
            if (!isNumber(set.swungLimit)) fail("marks", `${at}.swungLimit: degrees`)
            if (!isNumber(set.turnedLimit)) fail("marks", `${at}.turnedLimit: a number`)
            for (const [to, from] of entries(set.mirrorFacings)) if (!own(set.facings, from)) fail("marks", `${at}.mirrorFacings.${to}: no facing ${from} to mirror`)
            for (const [from, to] of entries(set.mirrorSides)) if (!(MARK_SIDES as readonly string[]).includes(from) || !(MARK_SIDES as readonly string[]).includes(to)) fail("marks", `${at}.mirrorSides.${from}: sides (${MARK_SIDES.join(", ")})`)
            if (!isObject(set.facings)) { fail("marks", `${at}.facings: expected facing -> marks`); continue }
            for (const [facing, list] of entries(set.facings)) {
                if (!Array.isArray(list)) { fail("marks", `${at}.facings.${facing}: a list of marks`); continue }
                list.forEach((m: any, i: number) => {
                    const where = `${at}.facings.${facing}[${i}]`
                    if (!isObject(m)) { fail("marks", `${where}: expected a mark`); return }
                    if (![...MARK_CENTERS, ...MARK_JOINTS].includes(m.on)) fail("marks", `${where}.on: one of ${[...MARK_CENTERS, ...MARK_JOINTS].join(", ")}`)
                    if (m.side !== undefined && !(MARK_SIDES as readonly string[]).includes(m.side)) fail("marks", `${where}.side: one of ${MARK_SIDES.join(", ")}`)
                    if ((MARK_JOINTS as readonly string[]).includes(m.on) && m.side === undefined) fail("marks", `${where}.side: a mark on a limb's joint says which limb`)
                    if (!Array.isArray(m.at) || m.at.length !== 2 || !m.at.every(Number.isInteger)) fail("marks", `${where}.at: [x, y] pixels`)
                    rowsOk(m.rows, markLetters, `${where}.rows`)
                })
            }
        }
        for (const [letter, ref] of letters(marks.eyeLetters)) if (!colorRef(ref)) fail("marks", `eyeLetters.${letter}: skin.<step>, eye.<name> or #rrggbb`)
        for (const k of ["frontTurn", "profileTurn", "faceDepth", "cheek"]) if (!isNumber(marks.eyePlacement?.[k])) fail("marks", `eyePlacement.${k}: a number`)
        if (!isObject(marks.eyeStyles)) fail("marks", "eyeStyles: expected name -> eye style")
        if (!own(marks.eyeStyles, marks.defaultEyes)) fail("marks", "defaultEyes: one of the eye styles")
        for (const [name, style] of entries(marks.eyeStyles)) {
            const at = `eyeStyles.${name}`
            if (!isObject(style)) { fail("marks", `${at}: expected an object`); continue }
            for (const [letter, ref] of letters(style.colors)) if (!colorRef(ref)) fail("marks", `${at}.colors.${letter}: skin.<step>, eye.<name> or #rrggbb`)
            const known = new Set([...letters(marks.eyeLetters), ...letters(style.colors)].map(([k]) => k))
            if (!Array.isArray(style.front) || style.front.length !== 2) fail("marks", `${at}.front: [screen-left eye, screen-right eye]`)
            else style.front.forEach((rows: unknown, i: number) => rowsOk(rows, known, `${at}.front[${i}]`))
            rowsOk(style.profile, known, `${at}.profile`)
            if (!isNumber(style.center?.x) || !isNumber(style.center?.y)) fail("marks", `${at}.center: { x, y } pixels`)
        }
    }

    // ---- styles -----------------------------------------------------------------------------------
    const styles = t.styles as any
    if (!isObject(styles)) fail("styles", "expected an object")
    else {
        if (styles.version !== 1) fail("styles", "version: expected 1")
        if (!own(styles.styles, styles.default)) fail("styles", "default: one of the styles")
        for (const [name, s] of entries(styles.styles)) {
            const at = `styles.${name}`
            if (!isObject(s)) { fail("styles", `${at}: expected an object`); continue }
            if (s.raster !== undefined && !isObject(s.raster)) fail("styles", `${at}.raster: settings over the engine's`)
            if (!skinSet(s.skins)) fail("styles", `${at}.skins: one of the skin sets (palettes.json skins)`)
            if (s.marks !== undefined && !own(marks?.markSets, s.marks)) fail("styles", `${at}.marks: one of the mark sets (marks.json markSets)`)
            if (s.eyes !== undefined && !own(marks?.eyeStyles, s.eyes)) fail("styles", `${at}.eyes: one of the eye styles (marks.json eyeStyles)`)
            if (s.clips !== undefined && !(CLIP_SET_NAMES as readonly string[]).includes(s.clips)) fail("styles", `${at}.clips: one of ${CLIP_SET_NAMES.join(", ")}`)
        }
    }

    // ---- humanoids --------------------------------------------------------------------------------
    const hum = t.humanoids as any
    if (!isObject(hum)) { fail("humanoids", "expected an object"); return problems }
    if (hum.version !== 1) fail("humanoids", "version: expected 1")
    const builds = isObject(hum.builds) ? hum.builds : {}
    for (const [name, b] of entries(builds)) {
        const at = `builds.${name}`
        if (!isObject(b)) { fail("humanoids", `${at}: expected an object`); continue }
        if (b.extends !== undefined && !own(builds, b.extends)) fail("humanoids", `${at}.extends: no build ${b.extends}`)
        if (b.proportions !== undefined && !isObject(b.proportions)) fail("humanoids", `${at}.proportions: name -> value`)
        if (b.scale !== undefined && (!isNumber(b.scale) || b.scale <= 0)) fail("humanoids", `${at}.scale: a positive number`)
        const seen = [name]
        for (let next = b.extends; typeof next === "string" && own(builds, next); next = builds[next].extends) {
            if (seen.includes(next)) { fail("humanoids", `${at}: extends itself (${[...seen, next].join(" -> ")})`); break }
            seen.push(next)
        }
    }
    const names = new Set<string>()
    const hairStyles = isObject((t.hair as any)?.styles) ? (t.hair as any).styles : undefined
    const noHair: unknown[] = Array.isArray((t.hair as any)?.none) ? (t.hair as any).none : []
    const equipment = isObject((t.equipment as any)?.models) ? (t.equipment as any).models : undefined
    const dyes = isObject(pal?.dyes?.colors) ? pal.dyes.colors : {}
    if (!Array.isArray(hum.humanoids)) fail("humanoids", "humanoids: a list")
    else hum.humanoids.forEach((h: any, i: number) => {
        const at = `humanoids[${i}]${typeof h?.name === "string" ? ` (${h.name})` : ""}`
        if (!isObject(h)) { fail("humanoids", `${at}: expected an object`); return }
        if (typeof h.name !== "string" || !/^[a-z][a-z0-9-]{1,47}$/.test(h.name)) fail("humanoids", `${at}.name: 2-48 lowercase letters, digits or -`)
        else if (names.has(h.name)) fail("humanoids", `${at}.name: named twice`)
        else names.add(h.name)
        const style = h.style ?? styles?.default
        if (!own(styles?.styles, style)) fail("humanoids", `${at}.style: one of the styles (styles.json)`)
        if (h.build !== undefined && !own(builds, h.build)) fail("humanoids", `${at}.build: no build ${h.build}`)
        if (h.proportions !== undefined && !isObject(h.proportions)) fail("humanoids", `${at}.proportions: name -> value`)
        if (h.posture !== undefined && !(POSTURES as readonly string[]).includes(h.posture)) fail("humanoids", `${at}.posture: one of ${POSTURES.join(", ")}`)
        if (h.frame !== undefined && (!Number.isInteger(h.frame) || h.frame < 64 || h.frame > 192)) fail("humanoids", `${at}.frame: 64-192 pixels`)
        const look = h.look
        if (!isObject(look)) { fail("humanoids", `${at}.look: expected an object`); return }
        const skins = own(styles?.styles, style) ? skinSet(styles.styles[style].skins) : undefined
        if (skins && !skins.has(look.skin)) fail("humanoids", `${at}.look.skin: no skin ${look.skin} in its style's skin set`)
        if (look.eyes !== undefined && !own(marks?.eyeStyles, look.eyes)) fail("humanoids", `${at}.look.eyes: one of the eye styles`)
        if (look.body !== undefined && isObject((t.bodies as any)?.models) && !own((t.bodies as any).models, look.body)) fail("humanoids", `${at}.look.body: no body model ${look.body}`)
        if (look.boneColor !== undefined && !own(pal?.ramps?.bones?.tones, look.boneColor)) fail("humanoids", `${at}.look.boneColor: one of the bone colours`)
        if (look.hair !== undefined) {
            if (hairStyles && !own(hairStyles, look.hair.style) && !noHair.includes(look.hair.style)) fail("humanoids", `${at}.look.hair.style: no hair style ${look.hair.style}`)
            if (!own(pal?.hair?.colors, look.hair.color)) fail("humanoids", `${at}.look.hair.color: one of the hair colours`)
        }
        // Garments: a model of the slot (its own, a cut, or the slot's own), in a dye the game has (metal names a metal).
        const garments: [string, string, string][] = [["shirt", "shirt", "dye"], ["pants", "trousers", "dye"], ["shoes", "shoes", "dye"], ["belt", "belt", "dye"]]
        for (const [slot, fallback, color] of garments) {
            const g = look[slot]
            if (g === undefined) continue
            if (!isObject(g)) { fail("humanoids", `${at}.look.${slot}: expected an object`); continue }
            const model = g.model ?? g.cut ?? fallback
            if (equipment && !own(equipment[slot], model)) fail("humanoids", `${at}.look.${slot}: no ${slot} model ${model} (equipment.json)`)
            if (typeof g[color] !== "string") fail("humanoids", `${at}.look.${slot}.${color}: a colour name`)
            else if (model !== "greaves" && model !== "sabatons" && !own(dyes, g[color])) fail("humanoids", `${at}.look.${slot}.${color}: no dye ${g[color]}`)
        }
    })
    if (!Array.isArray(hum.races)) fail("humanoids", "races: a list of { race, sex?, body }")
    else hum.races.forEach((r: any, i: number) => {
        if (!isObject(r) || typeof r.race !== "string" || (r.sex !== undefined && typeof r.sex !== "string")) fail("humanoids", `races[${i}]: { race, sex?, body }`)
        else if (!names.has(r.body)) fail("humanoids", `races[${i}].body: no humanoid ${r.body}`)
    })
    const anySkin = new Set(entries(pal?.skins).flatMap(([name]) => [...(skinSet(name) ?? [])]))
    for (const [color, skin] of entries(hum.bodyColors?.skins)) if (!anySkin.has(skin)) fail("humanoids", `bodyColors.skins.${color}: no skin ${skin}`)
    for (const [beast, table] of entries(hum.bodyColors?.furs)) for (const [color, skin] of entries(table)) if (!anySkin.has(skin)) fail("humanoids", `bodyColors.furs.${beast}.${color}: no skin ${skin}`)
    if (!isObject(hum.bodyColors?.skins) || !isObject(hum.bodyColors?.furs)) fail("humanoids", "bodyColors: { skins, furs }")
    return problems
}
